import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'

import styles from './styles.module.scss'

import Button from '../../Button/Button'
import Checkbox from '../../../Core/AppCheckbox'

const formConfig = {
  max_total_minutes_per_day: {
    title: 'Work hours limit per day',
    type: 'number'
  },
  max_minutes_per_week: {
    title: 'Work hours limit per week',
    type: 'number'
  },
  max_minutes_per_rolling_7_days: {
    title: 'Work hours limit per 7 days in a row',
    type: 'number'
  },
  max_consecutive_working_days: {
    title: 'Can work consecutive days per week',
    type: 'number',
    divider: true,
    unit: 'days',
  },
  night_hours: {
    title: 'Night hours',
    type: 'checkbox',
  },
  night_cap_minutes_per_day: {
    title: 'No more night hours than',
    type: 'number',
    divider: true,
  },
  min_rest_minutes_between_sessions: {
    title: 'Minimum rest hours between work',
    type: 'number',
  },
  min_non_working_minutes_per_rolling_7_days: {
    title: 'Total non-working hours per 7 days in a row',
    type: 'number',
    divider: true,
  },
  age18: {
    title: 'Apply rule for under 18 age, pregnant, breastfeeding',
    description: '(can’t work more than X hours per day, can’t work on Sundays, can’t work after 22:00 till 06:00)',
    type: 'checkbox',
  },
  work_time_per_day: {
    title: 'Work time per day',
    type: 'number',
  },
  min_consecutive_non_working_minutes: {
    title: 'Consecutive non-breakable non-working hours',
    type: 'number',
  },
  consectutive_non_working_days_per_week: {
    title: 'Consecutive non-breakable non-working days per week (Sunday included)',
    type: 'number',
  },
  generate_with_demand: {
    title: 'Generate with demand planning data included',
    type: 'checkbox',
  },
  generate_with_time_off: {
    title: 'Generate with time off data included',
    type: 'checkbox',
  },
  generate_with_accumulated_hours: {
    title: 'Generate with accumulated hours data and limits included',
    type: 'checkbox',
  },
}

const GenerationAITimeSheet = forwardRef(({companyId, onSubmit}, ref) => {
  const { t } = useTranslation()

  const [settings, setSettings] = useState({
    max_total_minutes_per_day: 720,
    max_minutes_per_week: 3120,
    max_minutes_per_rolling_7_days: 3600,
    max_consecutive_working_days: 6,
    night_hours: false,
    night_cap_minutes_per_day: 120,
    min_rest_minutes_between_sessions: 660,
    min_consecutive_non_working_minutes: 720,
    min_non_working_minutes_per_rolling_7_days: 2100,
  })
  const [form, setForm] = useState({name: '', generated_json: ''})
  const [open, setOpen] = useState(false)

  const originalSheetRef = useRef(null)

  useImperativeHandle(ref, () => ({
    open: (originalSheet) => {
      originalSheetRef.current = originalSheet
      setOpen(true)
    }
  }))

  const handleChangeForm = (field) => (value, units) => {
    if (typeof value === 'string') {
      value = value.replace(/\D/g, '')
      value = value ? parseInt(value, 10) : ''
      if (units !== 'days') {
        value = value * 60
      }
    }
    setSettings({
      ...settings,
      [field]: value,
    })
  }

  const handleBlur = (field) => (e) => {
    if (e.target.value === '') {
      handleChangeForm(field)(0)
    }
  }

  const handleCopyOriginalData = () => {
    navigator.clipboard.writeText(JSON.stringify(originalSheetRef.current || {}, null, 2))
  }

  const handleSubmit = async () => {
    const post = {
      options: settings,
      name: form.name,
      results: form.generated_json,
      data: originalSheetRef.current,
    }
    onSubmit(post)
    setOpen(false)
  }

  return (
    <Dialog onClose={() => setOpen(false)} open={open} maxWidth="md">
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.icon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={33}
              height={33}
              fill="none"
            >
              <path
                fill="#E2EBF4"
                fillOpacity={0.85}
                d="M16.108 32.191A16.2 16.2 0 0 0 32.2 16.087 16.212 16.212 0 0 0 16.092 0 16.2 16.2 0 0 0 0 16.087a16.216 16.216 0 0 0 16.108 16.104ZM7.99 17.88a1.132 1.132 0 0 1-1.146-1.162 1.123 1.123 0 0 1 1.146-1.129h6.956V6.242a1.137 1.137 0 0 1 1.146-1.145 1.137 1.137 0 0 1 1.146 1.146v10.476a1.13 1.13 0 0 1-1.146 1.161H7.99Z"
              />
            </svg>
          </div>
          <div className={styles.content}>
            <div className={styles.title}>{ t('TimeSheet Re-Generate') }</div>
            <div className={styles.label}>{ t('For employees with full work status:') }</div>
            {
              Object.entries(settings).map(([key, value], index) => {
                const config = formConfig[key]
                if (!config) return null
                
                return (
                  <Fragment key={key}>
                    <div className={styles.row}>
                      {
                        (type => {
                          switch (type) {
                            case 'checkbox':
                              return (
                                <>
                                  <Checkbox checked={value} onChange={handleChangeForm(key)} />
                                  <div className={styles.label}>
                                    <div>{ t(config.title) }</div>
                                    {
                                      config.description && <div style={{fontSize: 10}}>{ t(config.description) }</div>
                                    }
                                  </div>
                                </>
                              )
                            case 'number':
                              value = config.unit === 'days' ? value : value / 60
                              return (
                                <>
                                  <div className={styles.label}>{ t(config.title) }:</div>
                                  <input
                                    value={value}
                                    onChange={e => handleChangeForm(key)(e.target.value, config.unit)}
                                    className={styles.input}
                                    onBlur={handleBlur(key)}
                                    type="text" />
                                </>
                              )
                            default:
                              return null
                          }
                        })(config.type) 
                      }
                    </div>
                    {
                      config.divider && <div className={styles.divider} />
                    }
                  </Fragment>
                )
              })
            }
            <div className={styles.inputGroup}>
              <div className={styles.label}>{t('Name of generation')}</div>
              <input
                className={styles.input}
                value={form.name}
                onChange={({target}) => setForm(prev => ({...prev, name: target.value}))} />
            </div>
            <div className={styles.inputGroup}>
              <div className={styles.row}>
                <div className={styles.label}>{t('AI results:')}</div>
                <div className={styles.copy} onClick={handleCopyOriginalData}>
                  { t('Copy original data') }
                </div>
              </div>
              <textarea
                className={styles.textarea}
                value={form.generated_json}
                onChange={({target}) => setForm(prev => ({...prev, generated_json: target.value}))} />
            </div>
          </div>
        </div>
        <Button onClick={handleSubmit} size="large">
          { t('Generate') }
        </Button>
      </div>
    </Dialog>
  )
})

export default GenerationAITimeSheet
